import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';

const ITEMS_PER_PAGE = 20;

// Mirrors Video#requires_purchase? / #stripe_synced? on the backend.
const isPriced = (video) => video.price_cents != null && video.price_cents >= 50;
const isStripeSynced = (video) =>
  Boolean(video.stripe_product_id && video.stripe_price_id) &&
  video.stripe_price_cents === video.price_cents;

// Existing row controls must not also open the sales panel (row click does).
const stop = (fn) => (e) => {
  e.stopPropagation();
  fn(e);
};

const VideosTable = ({ videos, onEdit, onDelete, onToggleFeature, onOpenSales }) => {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const debounceRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchInput]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Filter videos by search
  const filteredVideos = useMemo(() => {
    if (!debouncedSearch.trim()) return videos;
    const query = debouncedSearch.toLowerCase();
    return videos.filter((v) => v.title.toLowerCase().includes(query));
  }, [videos, debouncedSearch]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredVideos.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedVideos = filteredVideos.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  const showPagination = filteredVideos.length > ITEMS_PER_PAGE;

  if (videos.length === 0) {
    return (
      <div className="bg-battle-gray rounded-lg overflow-hidden border border-army/20">
        <div className="p-12 text-center text-gray-500">
          No videos yet. Add your first battle!
        </div>
      </div>
    );
  }

  return (
    <div className="bg-battle-gray rounded-lg overflow-hidden border border-army/20">
      {/* Search Input */}
      <div className="p-4 border-b border-army/15">
        <input
          type="text"
          placeholder="Search videos..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full sm:w-80 px-4 py-2 bg-battle-dark border border-army/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-yellow/60 transition-colors"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead className="bg-battle-dark">
            <tr>
              <th className="text-left p-4 text-gray-400 font-semibold w-12"></th>
              <th className="text-left p-4 text-gray-400 font-semibold">Title</th>
              <th className="text-left p-4 text-gray-400 font-semibold">Artists</th>
              <th className="text-left p-4 text-gray-400 font-semibold">Battle</th>
              <th className="text-left p-4 text-gray-400 font-semibold">Platform</th>
              <th className="text-left p-4 text-gray-400 font-semibold">Status</th>
              <th className="text-right p-4 text-gray-400 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedVideos.map((video) => (
              <tr
                key={video.id}
                className="border-t border-army/10 hover:bg-gray-800/50 transition-colors cursor-pointer"
                data-testid={`video-row-${video.id}`}
                onClick={onOpenSales ? () => onOpenSales(video) : undefined}
                title={onOpenSales ? 'Open sales analytics' : undefined}
              >
                {/* Feature Toggle Star */}
                <td className="p-4">
                  <button
                    onClick={stop(() => onToggleFeature(video.id, video.featured))}
                    className={`text-xl transition-colors ${
                      video.featured ? 'text-brand-yellow hover:text-brand-yellow-light' : 'text-gray-600 hover:text-gray-400'
                    }`}
                    data-testid={`feature-toggle-${video.id}`}
                    title={video.featured ? 'Remove from featured' : 'Add to featured'}
                  >
                    {video.featured ? '★' : '☆'}
                  </button>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/video/${video.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-white hover:text-brand-yellow transition-colors"
                    >
                      {video.title}
                    </Link>
                    {video.featured && (
                      <span className="px-1.5 py-0.5 bg-brand-yellow/15 text-brand-yellow text-xs font-bold rounded">
                        FEATURED
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-4 text-gray-400">
                  {video.artists?.map(a => a.display_name || a.username).join(', ') || 'None'}
                </td>
                <td className="p-4">
                  {/* Quick-action: clicking opens the edit modal's battle
                      controls (checkbox + format), the single source of
                      truth for designating/toggling battle status. */}
                  <button
                    onClick={stop(() => onEdit(video))}
                    className={`px-2 py-1 rounded text-xs font-bold transition-colors ${
                      video.is_battle
                        ? 'bg-brand-yellow/15 text-brand-yellow border border-brand-yellow/35 hover:bg-brand-yellow/25'
                        : 'bg-battle-dark text-gray-500 border border-army/20 hover:text-gray-300'
                    }`}
                    data-testid={`battle-designation-${video.id}`}
                    title="Set battle status / format"
                  >
                    {video.is_battle ? (video.battle_format ? video.battle_format.toUpperCase() : 'BATTLE') : 'Not a battle'}
                  </button>
                </td>
                <td className="p-4">
                  <span className="px-2 py-1 bg-battle-dark rounded text-sm capitalize text-gray-300">
                    {video.platform}
                  </span>
                </td>
                <td className="p-4">
                  <span
                    className={`px-2 py-1 rounded text-sm capitalize ${
                      video.status === 'exclusive'
                        ? 'bg-army text-white'
                        : 'bg-green-900 text-green-300'
                    }`}
                  >
                    {video.status}
                  </span>
                  {isPriced(video) && (
                    <span
                      className={`ml-2 px-2 py-1 rounded text-xs font-bold whitespace-nowrap ${
                        isStripeSynced(video)
                          ? 'bg-green-900 text-green-300'
                          : 'bg-brand-yellow/15 text-brand-yellow border border-brand-yellow/35'
                      }`}
                      data-testid={`stripe-badge-${video.id}`}
                      title={isStripeSynced(video)
                        ? 'Stripe Product and Price match the current price'
                        : 'Stripe Product/Price missing or out of date; run Sync Stripe products'}
                    >
                      {isStripeSynced(video) ? 'Stripe synced' : 'Stripe pending'}
                    </span>
                  )}
                </td>
                <td className="p-4 text-right whitespace-nowrap">
                  {onOpenSales && (
                    <button
                      onClick={stop(() => onOpenSales(video))}
                      className="text-brand-yellow hover:text-brand-yellow-light mr-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow/60 rounded"
                      data-testid={`sales-video-${video.id}`}
                    >
                      Sales
                    </button>
                  )}
                  <button
                    onClick={stop(() => onEdit(video))}
                    className="text-blue-400 hover:text-blue-300 mr-3"
                    data-testid={`edit-video-${video.id}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={stop(() => onDelete(video.id))}
                    className="text-red-400 hover:text-red-300"
                    data-testid={`delete-video-${video.id}`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {showPagination && (
        <div
          className="flex items-center justify-center gap-4 p-4 border-t border-army/15"
          data-testid="pagination-controls"
        >
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              currentPage === 1
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-gray-700 text-white hover:bg-gray-600 border border-gray-600'
            }`}
            data-testid="pagination-prev"
          >
            Previous
          </button>
          <span className="text-gray-400 text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              currentPage === totalPages
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-army text-white hover:bg-army-light'
            }`}
            data-testid="pagination-next"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default VideosTable;
